'use client'

import { sendGTMEvent } from '@next/third-parties/google'
import { useSearchParams } from 'next/navigation'

import { useCallback, useEffect, useRef } from 'react'

import isArray from 'lodash/fp/isArray'
import isEmpty from 'lodash/fp/isEmpty'
import isFunction from 'lodash/fp/isFunction'
import isNil from 'lodash/fp/isNil'
import noop from 'lodash/fp/noop'
import omit from 'lodash/fp/omit'
import pick from 'lodash/fp/pick'

import type { EventOptions } from '../ampli'

import { env, isDev } from '../helpers'
import useAmplitude from './useAmplitude'
import useLogger from './useLogger'
import useUser from './useUser'

export default function useTracking(
  events?: string | Array<string>,
  payload?: Record<string, unknown>,
  {
    amplitude: withAmplitude = true,
    gtm: withGtm = true,
    waitForUser = true,
    withUser = true,
  } = {},
) {
  const searchParams = useSearchParams()

  const { isLoading: isUserLoading, user } = useUser()

  const logger = useLogger()

  const environment = env()

  const isDebuggingTracking =
    (process.env.DEBUG_TRACKING || process.env.NEXT_PUBLIC_DEBUG_TRACKING) ===
      'true' ||
    !isNil(searchParams.get('debug')) ||
    !isNil(searchParams.get('debugTracking'))
  const allowTracking =
    !isDev() ||
    isDebuggingTracking ||
    (process.env.ALLOW_TRACKING || process.env.NEXT_PUBLIC_ALLOW_TRACKING) ===
      'true' ||
    searchParams.get('allowTracking') === 'true'

  const { amplitude } = useAmplitude({ waitForUser })

  const eventTracked = useRef<
    Record<string, { amplitude: boolean; gtm: boolean }>
  >({})

  const customTrack = useCallback(
    (
      eventName: string | undefined,
      eventPayload?: Record<string, unknown>,
      eventOptions?: {
        force?: boolean
        withAmplitude?: boolean
        withGtm?: boolean
        withUser?: boolean
      } & EventOptions,
      callback?: () => void,
    ) => {
      if (isEmpty(eventName) || (waitForUser && isUserLoading)) {
        return
      }

      eventTracked.current[eventName] ??= {
        amplitude: false,
        gtm: false,
      }

      const shouldTrackWithAmplitude =
        withAmplitude && eventOptions?.withAmplitude !== false
      const shouldTrackWithGtm = withGtm && eventOptions?.withGtm !== false

      const alreadySent =
        shouldTrackWithAmplitude && shouldTrackWithGtm
          ? eventTracked.current[eventName].amplitude &&
            eventTracked.current[eventName].gtm
          : shouldTrackWithAmplitude
            ? eventTracked.current[eventName].amplitude
            : shouldTrackWithGtm
              ? eventTracked.current[eventName].gtm
              : true

      if (alreadySent && !eventOptions?.force) {
        return
      }

      const actualPayload: Record<string, string> = {
        ...eventPayload,
        environment,
        // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/userAgent
        userAgent:
          typeof window !== 'undefined' ? window.navigator.userAgent : '',
      }

      if (withUser && eventOptions?.withUser !== false && !isEmpty(user)) {
        actualPayload.email = user.email || (eventPayload?.email as string)
        actualPayload.id = user.uuid || (eventPayload?.id as string)
      }

      if (allowTracking && typeof window !== 'undefined') {
        if (
          shouldTrackWithAmplitude &&
          isFunction(amplitude?.track) &&
          (!eventTracked.current[eventName].amplitude || eventOptions?.force)
        ) {
          amplitude.track(
            eventName,
            actualPayload,
            omit(
              ['force', 'withAmplitude', 'withGtm', 'withUser'],
              eventOptions,
            ),
          )

          eventTracked.current[eventName].amplitude = true
        }

        if (
          shouldTrackWithGtm &&
          isFunction(sendGTMEvent) &&
          (!eventTracked.current[eventName].gtm || eventOptions?.force)
        ) {
          sendGTMEvent({ event: eventName, ...actualPayload })

          eventTracked.current[eventName].gtm = true
        }
      }

      if (
        (!allowTracking && typeof window !== 'undefined') ||
        isDebuggingTracking
      ) {
        const isEventSent =
          shouldTrackWithAmplitude && shouldTrackWithGtm
            ? eventTracked.current[eventName].amplitude &&
              eventTracked.current[eventName].gtm
            : shouldTrackWithAmplitude
              ? eventTracked.current[eventName].amplitude
              : shouldTrackWithGtm
                ? eventTracked.current[eventName].gtm
                : true

        if ((!allowTracking || isEventSent) && isFunction(callback)) {
          callback()
        }

        logger.debug(
          `[TRACKING] Tracking "${eventName}" event ${isEventSent ? 'sent' : 'pending'}`,
          {
            eventName,
            eventOptions: pick(
              ['force', 'withAmplitude', 'withGtm', 'withUser'],
              eventOptions,
            ),
            eventTracked: eventTracked.current[eventName],
            hasAmplitude: isFunction(amplitude?.track),
            isEventSent,
            options: omit(
              ['force', 'withAmplitude', 'withGtm', 'withUser'],
              eventOptions,
            ),
            payload: actualPayload,
            user: { isLoading: isUserLoading, user },
            waitForUser,
            withAmplitude,
            withGtm,
            withUser,
          },
        )

        if (!allowTracking) {
          eventTracked.current[eventName] = {
            amplitude: true,
            gtm: true,
          }
        }
      }
    },
    [
      allowTracking,
      amplitude,
      environment,
      isDebuggingTracking,
      isUserLoading,
      logger,
      user,
      waitForUser,
      withAmplitude,
      withGtm,
      withUser,
    ],
  )

  useEffect(() => {
    if (!isEmpty(events)) {
      const eventsToHandle = isArray(events) ? events : [events]

      eventsToHandle.forEach((eventName) => {
        customTrack(eventName, payload)
      })
    }
  }, [customTrack, events, payload])

  return {
    amplitude,
    gtm: {
      sendGTMEvent:
        typeof window !== 'undefined' && isFunction(sendGTMEvent)
          ? sendGTMEvent
          : noop,
    },
    track: customTrack,
  }
}
